import { Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { PatientService } from 'src/app/services/patient/patient.service';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { ResultatPatient } from 'src/app/utils/interfaces/new/resultat-patient.interface';
import { environment } from 'src/environments/environment';

interface PatientTableDisplay {
  patientData: string;
  antecedent: string;
  id: string;
  meta: ResultatPatient;
}

interface SelectPatient {
  doSelect: boolean;
  patientId: string;
}

interface SortPatient {
  sortOrder: 'ASC' | 'DESC';
  sortBy: 'patientData' | 'antecedent';
}

@Component({
  selector: 'app-modal-patients-by-antecedents',
  templateUrl: './modal-patients-by-antecedents.component.html',
  styleUrls: [
    './modal-patients-by-antecedents.component.scss',
    '../../fragments/modal/modal.fragment.scss'
  ]
})
export class ModalPatientsByAntecedentsComponent implements OnInit, OnDestroy {

  @ViewChild('modalBody') public modalBody: ElementRef;
  @ViewChild('outsideModal') public outsideModal: ElementRef;

  public closeEmitter = new EventEmitter();
  public patientsTable: Paginated<PatientTableDisplay>;
  public patientsTableColumn = [
    { title: 'Patient', data: 'patientData' },
    { title: 'Antécédent', data: 'antecedent' }
  ];
  public selectedPatients: Array<PatientTableDisplay> = [];
  public inputSearchForm: FormGroup;
  private _formChangeSubscribeValue: Subscription;
  public noResultMsg = '';

  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event): void {
    if (!environment.closeModalOnClickOutside) {
      return;
    }

    if (!this.modalBody.nativeElement.contains(event.target) && this.outsideModal.nativeElement.contains(event.target)) {
      this.closeModal();
    }
  }

  constructor(
    private _patientSrv: PatientService,
    private _formBuilder: FormBuilder
  ) { }

  async ngOnInit(): Promise<void> {
    this._initForm();
  }

  ngOnDestroy(): void {
    if (this._formChangeSubscribeValue) {
      this._formChangeSubscribeValue.unsubscribe();
    }
  }

  public closeModal(returnPatients = false): void {
    const patients: Array<ResultatPatient> = [];
    if (returnPatients && this.selectedPatients.length > 0) {
      for (const sp of this.selectedPatients) {
        patients.push(sp.meta);
      }
    }

    this.closeEmitter.emit(patients.length > 0 ? patients : undefined);
  }

  public addRemovePatient(event: PatientTableDisplay): void {
    if (!this.selectedPatients.find(e => e.id === event.id)) {
      this._doSelectPatient({ doSelect: true, patientId: event.id });
      this.selectedPatients.push(event);
    } else {
      this._doSelectPatient({ doSelect: false, patientId: event.id });
      const index = this.selectedPatients.findIndex(e => e.id === event.id);
      this.selectedPatients.splice(index, 1);
    }
  }

  public sortPatients(sort: SortPatient): void {
    if (this.patientsTable && this.patientsTable.objects && this.patientsTable.objects.length > 0) {
      this.patientsTable.objects.sort((a, b) => {
        if (a[sort.sortBy] > b[sort.sortBy]) {
          return sort.sortOrder === 'ASC' ? 1 : -1;
        } else if (a[sort.sortBy] < b[sort.sortBy]) {
          return sort.sortOrder === 'ASC' ? -1 : 1;
        } else {
          return 0;
        }
      });
    }
  }

  private _initForm(): void {
    this.inputSearchForm = this._formBuilder.group({
      searchInput: ['']
    });

    this._formChangeSubscribeValue = this.inputSearchForm.valueChanges.pipe(debounceTime(500)).subscribe(async (event) => {
      await this._doSearchPatient(event.searchInput);
    });
  }

  private async _addPatientToDisplayTable(patient: ResultatPatient): Promise<void> {
    const antecedent = (await this._patientSrv.findOnePatientAntecedentsPerso(patient.id)).objects;
    for (const a of antecedent) {
      const patientTableDisplay: PatientTableDisplay = {
        id: patient.id,
        meta: patient,
        patientData: patient.nomPrenomDateNaissance,
        antecedent: a.antecedent?.libelle || ''
      };

      if (this.patientsTable) {
        this.patientsTable.objects.push(patientTableDisplay);
      } else {
        this.patientsTable = {
          objects: [patientTableDisplay],
          page: 0,
          perpage: 0,
          total: 0
        };
      }
    }
  }

  private async _doSearchPatient(antecedent: string): Promise<void> {
    let result: Paginated<ResultatPatient> = {
      objects: [], page: 0, perpage: 0, total: 0
    };

    if (antecedent.length >= 3) {
      result = await this._patientSrv.patientsSearchFilters({
        getArchive: false,
        antecedent
      });
    } else {
      return;
    }
    this.selectedPatients = [];

    if (this.patientsTable && this.patientsTable.objects) {
      this.patientsTable.objects = [];
    }

    if (!result || !result.objects || result.objects.length === 0) {
      this.noResultMsg = 'Aucun patient trouvé';
    } else {
      this.noResultMsg = '';
    }

    for (const r of result.objects) {
      this._addPatientToDisplayTable(r);
    }
  }

  private _doSelectPatient(patient: SelectPatient): void {
    if (this.patientsTable && this.patientsTable.objects) {
      for (const [index, patientDisplayTable] of this.patientsTable.objects.entries()) {
        if (patientDisplayTable.id === patient.patientId) {
          if (patient.doSelect) {
            Object.defineProperty(patientDisplayTable, 'rowColor', { configurable: true, value: 'var(--primary)' });
            Object.defineProperty(patientDisplayTable, 'textColor', { configurable: true, value: 'var(--white)' });
          } else {
            Object.defineProperty(patientDisplayTable, 'rowColor', { configurable: true, value: 'transparent' });
            Object.defineProperty(patientDisplayTable, 'textColor', { configurable: true, value: 'var(--black)' });
          }

          this.patientsTable.objects.splice(index, 1, patientDisplayTable);
        }
      }
    }
  }

}
